/**
 * SecurityAlertMutes.jsx — the "verified travel" control for the unexpected-country
 * security alert, shown above the access log's Security alerts view.
 *
 * The alert raises an IT ticket the first time someone signs in from outside the
 * expected countries, then repeats once per user+country per 24h. For genuine
 * travel that is a fresh ticket every day, which trains people to ignore it. Once
 * IT has verified the person, a SuperAdmin records a mute here.
 *
 * Muting suppresses the EMAIL only — the sign-in is still logged, so the audit
 * trail stays complete. Every mute expires; there is no permanent option.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { getAccessAlertMutes, createAccessAlertMute, revokeAccessAlertMute } from '../api/client.js';

const DURATIONS = [7, 30, 90];

const fmtUntil = (iso) => {
  try { return new Date(iso).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return iso; }
};

export default function SecurityAlertMutes({ defaultEmail = '', defaultCountry = '' }) {
  const [mutes, setMutes] = useState([]);
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState(defaultEmail);
  const [country, setCountry] = useState(defaultCountry);
  const [days, setDays] = useState(30);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Arriving from a clicked sign-in row: adopt that person and country, and open
  // the form so the next action is one click rather than a hunt for the button.
  useEffect(() => {
    if (!defaultEmail) return;
    setEmail(defaultEmail);
    setCountry(defaultCountry || '');
    setOpen(true);
  }, [defaultEmail, defaultCountry]);

  const load = useCallback(() => {
    getAccessAlertMutes()
      .then(r => setMutes(Array.isArray(r?.mutes) ? r.mutes : []))
      .catch(e => setError(e.message || 'Could not load mutes.'));
  }, []);
  useEffect(() => { load(); }, [load]);

  async function save() {
    setBusy(true); setError(null);
    try {
      await createAccessAlertMute({ email: email.trim(), country: country.trim() || null, days, reason });
      setOpen(false); setReason(''); setCountry(''); load();
    } catch (e) { setError(e.message || 'Could not save the mute.'); }
    finally { setBusy(false); }
  }

  async function lift(id) {
    setBusy(true); setError(null);
    try { await revokeAccessAlertMute(id); load(); }
    catch (e) { setError(e.message || 'Could not lift the mute.'); }
    finally { setBusy(false); }
  }

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 12, marginBottom: 12, background: 'var(--surface-2, var(--surface))' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <strong style={{ fontSize: 13 }}>Verified travel</strong>
          <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>
            {mutes.length
              ? `${mutes.length} active mute${mutes.length === 1 ? '' : 's'} — sign-ins are still logged, only the IT email is suppressed.`
              : 'No active mutes. Once IT has verified someone’s travel, mute their alerts so the ticket isn’t raised daily.'}
          </div>
        </div>
        <button className="btn ghost sm" onClick={() => setOpen(o => !o)} style={{ fontSize: 12 }}>
          {open ? 'Cancel' : 'Mute alerts…'}
        </button>
      </div>

      {open && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginTop: 10 }}>
          <label style={{ fontSize: 11, color: 'var(--text-3)' }}>
            User email
            <input className="input" value={email} onChange={e => setEmail(e.target.value)}
              placeholder="person@therapieclinic.com" style={{ display: 'block', minWidth: 240, fontSize: 12 }} />
          </label>
          <label style={{ fontSize: 11, color: 'var(--text-3)' }}>
            Country
            <input className="input" value={country} onChange={e => setCountry(e.target.value.toUpperCase())}
              placeholder="Any" maxLength={2} style={{ display: 'block', width: 80, fontSize: 12 }} />
          </label>
          <label style={{ fontSize: 11, color: 'var(--text-3)' }}>
            For
            <select className="select" value={days} onChange={e => setDays(Number(e.target.value))}
              style={{ display: 'block', fontSize: 12 }}>
              {DURATIONS.map(d => <option key={d} value={d}>{d} days</option>)}
            </select>
          </label>
          <label style={{ fontSize: 11, color: 'var(--text-3)', flex: 1, minWidth: 200 }}>
            Reason
            <input className="input" value={reason} onChange={e => setReason(e.target.value)}
              placeholder="Verified with IT — travelling" style={{ display: 'block', fontSize: 12, width: '100%' }} />
          </label>
          <button className="btn primary sm" onClick={save} disabled={busy || !email.trim()} style={{ fontSize: 12 }}>
            {busy ? '…' : 'Mute'}
          </button>
          <div style={{ fontSize: 11, color: 'var(--text-3)', flexBasis: '100%' }}>
            Leave Country blank to cover a multi-country trip. Mutes always expire.
          </div>
        </div>
      )}

      {error && <div className="field-error" style={{ marginTop: 8 }}>{error}</div>}

      {mutes.length > 0 && (
        <ul style={{ listStyle: 'none', margin: '10px 0 0', padding: 0, display: 'grid', gap: 6 }}>
          {mutes.map(m => (
            <li key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, flexWrap: 'wrap' }}>
              <strong style={{ fontWeight: 500 }}>{m.email}</strong>
              <span style={{ color: 'var(--text-3)' }}>{m.country || 'any country'}</span>
              <span style={{ color: 'var(--text-3)' }}>until {fmtUntil(m.until)}</span>
              {m.reason && <span style={{ color: 'var(--text-3)', fontStyle: 'italic' }}>“{m.reason}”</span>}
              <button className="btn ghost sm" onClick={() => lift(m.id)} disabled={busy}
                style={{ fontSize: 11, color: 'var(--danger)' }}>Lift</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
